import { useState } from 'react'
import { Loading } from 'notiflix'
import { Report } from 'notiflix/build/notiflix-report-aio'
import api from '../../helpers/apiRequester'
import styles from './styles.module.scss'

// Today's date as a local 'YYYY-MM-DD' string (for the date input default + max).
const todayStr = (): string => {
    const now = new Date()
    const local = new Date(now.getTime() - now.getTimezoneOffset() * 60000)
    return local.toISOString().slice(0, 10)
}

// The day after `ymd` ('YYYY-MM-DD'), used as the exclusive upper bound.
const nextDay = (ymd: string): string => {
    const d = new Date(`${ymd}T00:00:00Z`)
    d.setUTCDate(d.getUTCDate() + 1)
    return d.toISOString().slice(0, 10)
}

// Public (all-users) Evans Hughes export: clients with a claim (fundable) who
// signed their CFA on a single selected day. Uses the same fundable predicate
// as the "Clients with Claims" stat, via POST /mfc-clients-day-report.
const MfcDayReportDownload = () => {
    const [selectedDate, setSelectedDate] = useState<string>(todayStr)
    const [loading, setLoading] = useState(false)
    const [dailyLoading, setDailyLoading] = useState(false)

    const anyLoading = loading || dailyLoading

    // Fetch the generated CSV and trigger a blob download via an anchor element
    // rather than window.open (which iOS Safari blocks after an await).
    const triggerBlobDownload = async (fileUrl: string, filename: string) => {
        const csvText = await fetch(fileUrl).then((r) => r.text())
        const blob = new Blob([csvText], { type: 'text/csv;charset=utf-8;' })
        const blobUrl = URL.createObjectURL(blob)
        const a = document.createElement('a')
        a.href = blobUrl
        a.download = filename
        document.body.appendChild(a)
        a.click()
        document.body.removeChild(a)
        URL.revokeObjectURL(blobUrl)
    }

    const downloadDay = async () => {
        if (!selectedDate) return

        setLoading(true)
        Loading.standard('Generating report...')

        try {
            const fromDate = `${selectedDate} 00:00:00`
            const toDate = `${nextDay(selectedDate)} 00:00:00`

            const response = await api({ url: '/mfc-clients-day-report', data: { fromDate, toDate } })
            const fileUrl = response.data?.fileUrl
            if (!fileUrl) {
                Report.failure('Error', 'No file URL received from server', 'OK')
                return
            }

            const filename = `evans-hughes-clients-${selectedDate}.csv`
            await triggerBlobDownload(fileUrl, filename)

            Report.success('Success', 'Report generated successfully!', 'OK')
        } catch (error: any) {
            console.error('Error downloading MFC day report:', error)
            Report.failure('Error', error?.response?.data?.message || 'Failed to generate report. Please try again.', 'OK')
        } finally {
            setLoading(false)
            Loading.remove()
        }
    }

    const downloadDailyStats = async () => {
        setDailyLoading(true)
        Loading.standard('Generating report...')

        try {
            const response = await api({ url: '/mfc-daily-claim-stats', data: {} })
            const fileUrl = response.data?.fileUrl
            if (!fileUrl) {
                Report.failure('Error', 'No file URL received from server', 'OK')
                return
            }

            const filename = `evans-hughes-daily-stats-${todayStr()}.csv`
            await triggerBlobDownload(fileUrl, filename)

            Report.success('Success', 'Report generated successfully!', 'OK')
        } catch (error: any) {
            console.error('Error downloading MFC daily stats:', error)
            Report.failure('Error', error?.response?.data?.message || 'Failed to generate report. Please try again.', 'OK')
        } finally {
            setDailyLoading(false)
            Loading.remove()
        }
    }

    return (
        <section className={styles.reportSection}>
            <h2 className={styles.sectionTitle}>Clients with Claims — Single Day</h2>
            <p className={styles.helpText}>
                Clients who signed their CFA and have a claim on the selected day.
            </p>
            <div className={styles.dayRow}>
                <input
                    type="date"
                    className={styles.dateInput}
                    value={selectedDate}
                    max={todayStr()}
                    onChange={(e) => setSelectedDate(e.target.value)}
                    disabled={anyLoading}
                />
                <button className={styles.downloadButton} onClick={downloadDay} disabled={anyLoading || !selectedDate}>
                    {loading ? 'Generating...' : 'Submit'}
                </button>
            </div>

            <h3 className={styles.subTitle}>Daily Stats</h3>
            <p className={styles.helpText}>
                Downloads a CSV with one row per day from 16 June to today, showing the total number of clients with a
                claim (fundable) who signed each day.
            </p>
            <div className={styles.dayRow}>
                <button className={styles.downloadButton} onClick={downloadDailyStats} disabled={anyLoading}>
                    {dailyLoading ? 'Generating...' : 'Download Daily Stats'}
                </button>
            </div>
        </section>
    )
}

export default MfcDayReportDownload
